import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import React, { useContext, useEffect, useState } from 'react'
import OfferCardMapper from './OfferCardMapper'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import useOffer from 'hooks/Offers/useOffer'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { isTourV1Offer } from 'lib/offer/offerTypes'
import CarouselCardLoadingSkeleton from 'components/OfferList/OfferCards/CarouselCardLoadingSkeleton'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import { useInView } from 'react-intersection-observer'
import { fadeIn } from 'components/utils/animations'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import useToggle from 'hooks/useToggle'

const Card = styled.div`
  display: grid;
  width: 75vw;
  height: 100%;
  flex-shrink: 0;
  min-height: ${rem(520)};

  > * {
    grid-column-start: 1;
    grid-row-start: 1;
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(320)};
  }
`

const Loader = styled(CarouselCardLoadingSkeleton)`
  opacity: 0;
  transition: opacity 0.2s;

  &.visible {
    opacity: 1;
  }
`

const OfferContent = styled(OfferCardMapper)`
  animation: ${fadeIn} 0.2s;
`

interface Props {
  id: string;
  idx?: number;
  className?: string;
  lazyLoad?: boolean;
  additionalOfferUrlParams?: string;
}

function OfferCard(props: Props) {
  const { id, idx, lazyLoad, additionalOfferUrlParams } = props
  const [offerId, setOfferId] = useState<string>(id)
  const onEvent = useContext(OfferListEventsContext)

  const [inViewRef, hasBeenInView] = useInView({
    triggerOnce: true,
    rootMargin: '100px',
  })

  const [offer,, offerError] = useOffer(offerId, {
    requireSummaryOnly: true,
    disabled: lazyLoad && !hasBeenInView,
  })

  const [loader,,, hideLoader] = useToggle(!offer)

  useEffect(() => {
    if (offer && hasBeenInView) {
      const params = {
        offer,
        position: idx,
        key: EventDataKey.ProductImpression,
      }
      onEvent(OfferListEvents.impression, params)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBeenInView, offer])

  useEffect(() => {
    if (isTourV1Offer(offer) && offer.altLink?.startsWith('/tour')) {
      // if we have an tourv2, we always want to show it instead of the v1 version
      // so if our altlink is for a tourv2, then lets swap to using that offer
      const tourv2Id = offer.altLink.replace('/tour/', '')
      if (tourv2Id) {
        setOfferId(tourv2Id)
      }
    }
  }, [offer])

  if (offerError) {
    return null
  }

  const pauseLoader = !hasBeenInView || !!offer

  return (
    <ErrorBoundary fallback={null}>
      <LoaderPlayStateContext.Provider value={pauseLoader ? 'paused' : 'running'}>
        <Card ref={inViewRef}>
          {loader && <Loader
            onTransitionEnd={hideLoader}
            className={!offer ? 'visible' : undefined}
          />}
          {!!offer && (hasBeenInView || !lazyLoad) && <OfferContent
            offer={offer}
            idx={idx}
            additionalOfferUrlParams={additionalOfferUrlParams}
          />}
        </Card>
      </LoaderPlayStateContext.Provider>
    </ErrorBoundary>
  )
}

export default React.memo(OfferCard)
